import { ResourceType } from '@dbt-labs/dbt-dag';

import { ResourceNodeType } from '@/__generated__/graphql';
import { isResourceType } from '@/util/resourceType';

export type ParsedUniqueId = {
  resourceType: ResourceType;
  packageName: string;
  /** This can be used for `name` in a pinch */
  remainder: string;
  raw: string;
};

/**
 * From a uniqueId, get the resourceType and packageName
 */
export const safeParseUniqueId = (uniqueId: string | undefined = '') => {
  let uniqueIdDecoded: string;
  try {
    uniqueIdDecoded = decodeURIComponent(uniqueId);
  } catch (e) {
    uniqueIdDecoded = uniqueId;
  }

  const parts = uniqueIdDecoded.split('.');
  if (!uniqueIdDecoded || !parts || parts.length < 3) {
    return {
      ok: undefined,
      err: `Invalid uniqueId, too few parts: ${uniqueIdDecoded}`,
    } as const;
  }
  const resourceType = parts[0];
  const packageName = parts[1];
  const remainder = parts.slice(2).join('.');
  if (!isResourceType(resourceType)) {
    return {
      ok: undefined,
      err: `Invalid resourceType: ${resourceType}`,
    } as const;
  }
  return {
    ok: {
      resourceType,
      packageName,
      remainder,
      raw: uniqueId,
    } as ParsedUniqueId,
    err: undefined,
  } as const;
};

export const parseUniqueId = (uniqueId: string | undefined) => {
  const result = safeParseUniqueId(uniqueId);
  if (result.err) {
    throw new Error(result.err);
  }
  return result.ok;
};

export const getDiscoveryResourceType = (
  uniqueId: string | undefined,
): ResourceNodeType | undefined => {
  const parsedId = safeParseUniqueId(uniqueId).ok;
  if (!parsedId) return;
  switch (parsedId.resourceType) {
    case 'exposure':
      return ResourceNodeType.Exposure;
    case 'macro':
      return ResourceNodeType.Macro;
    case 'metric':
      return ResourceNodeType.Metric;
    case 'model':
      return ResourceNodeType.Model;
    case 'seed':
      return ResourceNodeType.Seed;
    case 'semantic_model':
      return ResourceNodeType.SemanticModel;
    case 'snapshot':
      return ResourceNodeType.Snapshot;
    case 'source':
      return ResourceNodeType.Source;
    case 'test':
    case 'unit_test':
      return ResourceNodeType.Test;
    case 'saved_query':
      return ResourceNodeType.SavedQuery;
    case 'analysis':
    case 'function':
    case 'group':
      return undefined;
    default:
      const exhaustiveCheck: never = parsedId.resourceType;
      throw new Error(`Unhandled resourceType case: ${exhaustiveCheck}`);
  }
};
